
import { Button } from '@/core/components/ui/button';
import { RefreshCw, Download } from 'lucide-react';
import { SiteNode } from '../types/types';
import { EnergyColumnFilter } from './EnergyColumnFilter';
import { EnergyColumnVisibility } from './EnergyColumnDefinitions';
import { ChargingStatusFilter } from './ChargingStatusFilter';
import { AllocatedCurrentFilter } from './AllocatedCurrentFilter';
import { Switch } from '@/core/components/ui/switch';
import { Label } from '@/core/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/core/components/ui/radio-group';
import { ResultCount } from './molecules/ResultCount';

interface DashboardControlsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  data?: SiteNode[];
  onExport?: () => void;
  columnVisibility?: EnergyColumnVisibility;
  onColumnVisibilityChange?: (visibility: EnergyColumnVisibility) => void;
  selectedChargingStatuses?: string[];
  onSelectedChargingStatusesChange?: (statuses: string[]) => void;
  selectedAllocatedRanges?: string[];
  onSelectedAllocatedRangesChange?: (ranges: string[]) => void;
  excludeInactive?: boolean;
  onExcludeInactiveChange?: (value: boolean) => void;
  ocppConnected?: boolean;
  onOcppConnectedChange?: (value: boolean | undefined) => void;
  viewMode?: 'single' | 'operator';
  filteredCount?: number; // NEW: Count of filtered results
  isLoading?: boolean; // NEW: Loading state for result count
  unitMode?: 'A' | 'kW';
  onUnitModeChange?: (mode: 'A' | 'kW') => void;
  isExporting?: boolean;
}

export function DashboardControls({
  activeTab,
  onTabChange,
  onRefresh,
  isRefreshing = false,
  data,
  onExport,
  columnVisibility,
  onColumnVisibilityChange,
  selectedChargingStatuses = [],
  onSelectedChargingStatusesChange,
  selectedAllocatedRanges = [],
  onSelectedAllocatedRangesChange,
  excludeInactive = false,
  onExcludeInactiveChange,
  ocppConnected = false,
  onOcppConnectedChange,
  viewMode = 'single',
  filteredCount,
  isLoading = false,
  unitMode = 'A',
  onUnitModeChange,
  isExporting = false
}: DashboardControlsProps) {
  const tabs = ['Realtime Usage'];

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-0">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => onTabChange(tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-yellow-400 text-gray-900'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        
        <div className="flex gap-2">
          {filteredCount !== undefined && (
            <div className="flex items-center px-3 py-1">
              <ResultCount count={filteredCount} isLoading={isLoading} />
            </div>
          )}
          {onSelectedChargingStatusesChange && (
            <ChargingStatusFilter
              selectedStatuses={selectedChargingStatuses}
              onSelectedStatusesChange={onSelectedChargingStatusesChange}
            />
          )}
          {onSelectedAllocatedRangesChange && (
            <AllocatedCurrentFilter
              selectedRanges={selectedAllocatedRanges}
              onSelectedRangesChange={onSelectedAllocatedRangesChange}
            />
          )}
          {onExcludeInactiveChange && (
            <div className="flex items-center gap-2 px-3 py-1 border rounded-md bg-white">
              <Switch
                id="exclude-inactive"
                checked={excludeInactive}
                onCheckedChange={onExcludeInactiveChange}
              />
              <Label htmlFor="exclude-inactive" className="text-sm cursor-pointer font-normal">
                Hide Inactive
              </Label>
            </div>
          )}
          {onOcppConnectedChange && (
            <div className="flex items-center gap-2 px-3 py-1 border rounded-md bg-white">
              <Switch
                id="ocpp-connected"
                checked={ocppConnected}
                onCheckedChange={(checked) => onOcppConnectedChange(checked ? true : undefined)}
              />
              <Label htmlFor="ocpp-connected" className="text-sm cursor-pointer font-normal">
                OCPP Online
              </Label>
            </div>
          )}
          {onUnitModeChange && (
            <div className="flex items-center gap-1.5 px-3 py-1 border rounded-md bg-white">
              <RadioGroup
                value={unitMode}
                onValueChange={(v) => onUnitModeChange(v as 'A' | 'kW')}
                className="flex gap-3"
              >
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="A" id="unit-A" />
                  <Label htmlFor="unit-A" className="text-sm cursor-pointer font-normal">A</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <RadioGroupItem value="kW" id="unit-kW" />
                  <Label htmlFor="unit-kW" className="text-sm cursor-pointer font-normal">kW</Label>
                </div>
              </RadioGroup>
            </div>
          )}
          {columnVisibility && onColumnVisibilityChange && (
            <EnergyColumnFilter
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={onColumnVisibilityChange}
            />
          )}
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center space-x-2"
              onClick={onExport}
              disabled={!data || data.length === 0 || isExporting}
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="bg-white text-gray-600 border-gray-200 rounded-md h-8 w-8 p-0"
            onClick={onRefresh}
            disabled={!onRefresh || isRefreshing}
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      

    </div>
  );
}
